import { curveCatmullRom, line } from 'd3-shape';
import { isString, max, min } from 'lodash';
import svgPath from 'svgpath';
import { getFramework } from '@/framework';
// import { getBehavior, getFramework, getStageRect, getViewportRect } from '@/framework';
import { isHotKeyPress } from '@/utils/event-kit';
import {
    createForeignObjectImageUrl,
    createPointImage,
    exportSvgFirstPath,
    formatSvgPathDecimals,
    generateGradient,
    generateGradientToCtx,
    getGradientId,
    parseStrokeDasharray,
    serializerHtmlToString,
    serializeSvgText,
} from '@/utils/file-kit';
import { getLayerProps } from '@/utils/layer-kit';
import { getActualAngle, getPointsBetweenAngle } from '@/utils/math-kit';
import {
    BrushDasharrayType,
    BrushLinecapType,
    BrushType,
    LayerType,
    MaterialIdsType,
    StretchOptionType,
    type IBrushAttribute,
    type IBrushDrawConfig,
    type IBrushLayerProps,
    type IBrushPoint,
    type ILayerProps,
    type ILinearGradient,
    type IPoint,
    type ISize,
} from '@/version-types';

/**
 * 图层边缘间隙
 */
export const LAYER_EDGE_GAP = 2;
/**
 * 端点素材规范为 4px 大小
 */
export const BRUSH_POINT_SIZE = 4;

/**
 * 是否代码绘制 SVG
 *
 * @param type - 画笔类型
 */
export function isCodeSvg(type: BrushType) {
    return type === BrushType.Rect || type === BrushType.Ellipse;
}

/**
 * 是否波浪线 SVG
 *
 * @param type - 画笔类型
 */
export function isWavySvg(type: BrushType) {
    return type === BrushType.Wavy;
}

/**
 * 是否箭头笔 SVG
 *
 * @param type - 画笔类型
 */
export function isArrowSvg(type: BrushType) {
    return type === BrushType.Arrow;
}

/**
 * 是否曲线笔 SVG
 *
 * @param type - 画笔类型
 */
export function isCurveSvg(type: BrushType) {
    return type === BrushType.Line || type === BrushType.Curve;
}

/**
 * 是否多边形 SVG
 *
 * @param type - 画笔类型
 */
export function isPolygonSvg(type: BrushType) {
    return type === BrushType.Polygon;
}

/**
 * 获取空画笔属性
 *
 * @param color - 可选；颜色；默认黑色
 */
export function getDefaultBrushAttribute(color = '#000000ff') {
    return {
        color,
        dasharray: BrushDasharrayType.Solid,
        linecap: BrushLinecapType.Square,
        width: 0,
        offsetX: 0,
        offsetY: 0,
    };
}

/**
 * 获取默认端点信息
 */
export function getDefaultBrushPoint() {
    return {
        url: '',
        type: '',
        offset: 2,
        width: 0,
        height: 0,
    } as IBrushPoint;
}

/**
 * 获取空图层数据
 *
 * @param drawConfig - 绘制配置
 */
export function getBrushDefaultLayer(drawConfig?: IBrushDrawConfig) {
    return getLayerProps({
        layerType: LayerType.BRUSH_LAYER,
        data: {
            brushType: BrushType.Curve,
            /** 填充 */
            fills: [getDefaultBrushAttribute()],
            stroke: getDefaultBrushAttribute(),
            startPoint: getDefaultBrushPoint(),
            endPoint: getDefaultBrushPoint(),
            radius: 0,
            stretchOption: StretchOptionType.fill,

            imageTransform: {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },

            url: '',

            naturalWidth: 0,
            naturalHeight: 0,

            screenshotUrl: null,

            ...drawConfig,
        },
    });
}

/**
 * 抖动点剔除
 *
 * @param points - 坐标点
 * @param epsilon - 可选；点数
 */
export function simplifyPath(points: IPoint[], epsilon = 1) {
    // 找到最远的点
    let dMax = 0;
    let index = 0;
    const endIndex = points.length - 1;
    for (let i = 1; i < endIndex; i++) {
        const d = perpendicularDistance(points[i], points[0], points[endIndex]);
        if (d > dMax) {
            index = i;
            dMax = d;
        }
    }
    // 如果最远的点的距离大于epsilon，则递归地简化路径两边
    if (dMax > epsilon) {
        const recResults1 = simplifyPath(points.slice(0, index + 1), epsilon);
        const recResults2 = simplifyPath(points.slice(index, endIndex + 1), epsilon);
        // 结合两部分结果并返回
        return recResults1.slice(0, recResults1.length - 1).concat(recResults2);
    } else {
        return [points[0], points[endIndex]];
    }
}

/**
 * 计算点 p 到线段 v 和 w 的垂直距离
 *
 * @param p - 坐标点 p
 * @param v - 线段坐标点 v
 * @param w - 线段坐标点 w
 */
function perpendicularDistance(p: IPoint, v: IPoint, w: IPoint) {
    const vwLength = Math.sqrt((w.x - v.x) ** 2 + (w.y - v.y) ** 2);
    if (vwLength === 0) return Math.sqrt((p.x - v.x) ** 2 + (p.y - v.y) ** 2);

    const t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / vwLength ** 2;
    const tx = v.x + t * (w.x - v.x);
    const ty = v.y + t * (w.y - v.y);
    const closestPoint = { x: tx, y: ty };

    return Math.sqrt((p.x - closestPoint.x) ** 2 + (p.y - closestPoint.y) ** 2);
}

/**
 * 导出 SVG 里的路径
 *
 * @param text - 文本
 */
export const exportBrushPath = exportSvgFirstPath;

/**
 * 创建一个 SVG Path 路径
 *
 * @param fillAttr - 填充属性
 * @param pathD - 路径
 * @param width - 宽度
 * @param height - 高度
 * @param outDefs - 输出定义
 * @param strokeAttr - 可选；描边属性
 */
export function createPathTarget(
    fillAttr: IBrushAttribute,
    pathD: string,
    width: number,
    height: number,
    outDefs: string[],
    strokeAttr?: IBrushAttribute,
) {
    const attr = strokeAttr || fillAttr;
    const createColor = (attr: IBrushAttribute) => {
        const isGradient = !isString(attr.color);
        const gradientId = `ATTR_${getGradientId()}`;
        if (isGradient) {
            outDefs.push(generateGradient(gradientId, width, height, attr.color as ILinearGradient));
        }

        return isGradient ? `url(#${gradientId})` : (attr.color as string);
    };
    const stroke = createColor(attr);
    let fill = 'none';
    if (strokeAttr) {
        fill = createColor(fillAttr);
    }
    const strokeWidth = attr.width;
    const linecap = attr.linecap;
    const dasharray = parseStrokeDasharray(attr.dasharray, strokeWidth);
    return `<path d="${pathD}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-dasharray="${dasharray}" stroke-linecap="${linecap}" stroke-miterlimit="10" />`;
}

/**
 * 绘制一个 Canvas 路径
 *
 * @param ctx - Canvas 绘制上下文对象
 * @param pathD - 绘制路径
 * @param attr - 描边属性
 * @param width - 画布宽度
 * @param height - 画布高度
 * @param globalCompositeOperation - 画布合成属性
 * @param isMask - 可选；是否蒙版；默认值为 `false`
 */
export function createCtxTarget(
    ctx: CanvasRenderingContext2D,
    pathD: Path2D,
    attr: IBrushAttribute,
    width: number,
    height: number,
    globalCompositeOperation: string,
    isMask = false,
) {
    const isGradient = !isString(attr.color);
    ctx.save();
    ctx.globalCompositeOperation = globalCompositeOperation as any;
    ctx.lineWidth = attr.width;
    ctx.lineCap = attr.linecap;
    if (!isMask) {
        if (isGradient) {
            ctx.strokeStyle = generateGradientToCtx(ctx, width, height, attr.color as ILinearGradient);
        } else {
            ctx.strokeStyle = attr.color as string;
        }
        if (attr.dasharray) {
            ctx.setLineDash(parseStrokeDasharray(attr.dasharray, attr.width, false) as number[]);
        }
    } else {
        ctx.strokeStyle = attr.color as string;
    }
    ctx.stroke(pathD);
    ctx.restore();
}

/**
 * 创建 SVG 图片
 *
 * @param width - 宽度
 * @param height - 高度
 * @param paths - 绘制路径
 * @param defs - SVG 元素定义属性
 */
function createSvgImage(width: number, height: number, paths: string[], defs: string[] = []) {
    const html = `<svg viewBox="0 0 ${width} ${height}"
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    style="display:block">
    ${defs.length ? `<defs>${defs.join('')}</defs>` : ''}
    ${paths.join('')}
</svg>`;

    return {
        width: width,
        height: height,
        html,
        src: createForeignObjectImageUrl(width, height, serializerHtmlToString(serializeSvgText(html)), true),
    };
}

/**
 * 根据图层数据，获取路径偏移量
 *
 * @param fills - 画笔属性
 * @param stroke - 描边
 */
export function getPathOffset(fills: IBrushAttribute[], stroke: IBrushAttribute) {
    const fillWidth = fills[0].width;
    let offsetX = 0;
    let offsetY = 0;
    fills.forEach((item, i) => {
        if (i === 0) {
            offsetX += fillWidth;
            offsetY += fillWidth;
        } else {
            // 多重画笔，宽高需要加上偏移量
            offsetX += item.offsetX;
            offsetY += item.offsetY;
        }
    });
    const offsetStroke = LAYER_EDGE_GAP + stroke.width;
    const offsetFill = fillWidth / 2;
    return {
        /** 路径偏移量 */
        translate: offsetStroke + offsetFill,
        /** 宽度增加 */
        offsetWidth: offsetStroke * 2 + offsetX,
        /** 高度增加 */
        offsetHeight: offsetStroke * 2 + offsetY,
    };
}

/**
 * 绘制画笔描边
 *
 * @param isDrawPreview - 是否绘制预览
 * @param canvas - Canvas 绘制上下文对象
 * @param pathD - 绘制路径
 * @param data - 图层数据
 * @param rasterScale - 描边缓冲的栅格倍率，仅改变像素分辨率，不改变路径和线宽
 */
export async function drawBrushStroke(
    isDrawPreview: boolean,
    canvas: HTMLCanvasElement,
    pathD: string,
    data: Pick<IBrushLayerProps, 'fills' | 'stroke' | 'naturalWidth' | 'naturalHeight'> &
        Pick<ILayerProps, 'width' | 'height'>,
    rasterScale = 1,
) {
    const { fills, stroke, naturalWidth, naturalHeight, width, height } = data;
    canvas.width = Math.max(1, Math.floor(width * rasterScale));
    canvas.height = Math.max(1, Math.floor(height * rasterScale));
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    ctx?.scale(rasterScale, rasterScale);

    if (!isDrawPreview) {
        // 图层渲染时需要做偏移计算
        const res = getPathOffset(fills, stroke);
        const newWidth = width - res.offsetWidth;
        const newHeight = height - res.offsetHeight;
        pathD = formatSvgPathDecimals(
            svgPath(pathD)
                .scale(newWidth / naturalWidth, newHeight / naturalHeight)
                .translate(res.translate, res.translate)
                .toString(),
        );
    }
    const ctxStrokePath2d = new Path2D(pathD);

    // 创建描边
    if (ctx) {
        createCtxTarget(
            ctx,
            ctxStrokePath2d,
            { ...stroke, width: fills[0].width + stroke.width * 2 },
            naturalWidth,
            naturalHeight,
            'source-over',
        );
    }
    // 创建 mask, 绘制画笔镂空，保留描边
    if (ctx) {
        createCtxTarget(
            ctx,
            ctxStrokePath2d,
            {
                ...fills[0],
                color: '#000',
            },
            naturalWidth,
            naturalHeight,
            'destination-out',
            true,
        );
    }
}

/**
 * 获取端点的信息
 *
 * @param text - 文本
 * @param pointInfo - 画笔坐标点信息
 * @param fill - 画笔描边属性
 */
export function getArrowPointImage(text: string, pointInfo: IBrushPoint, fill: IBrushAttribute) {
    return createPointImage(
        text,
        pointInfo,
        {
            width: fill.width,
            color: fill.color as string,
        },
        BRUSH_POINT_SIZE,
    );
}

/**
 * 匹配重复的路径
 *
 * @param pathD - 绘制路径
 */
export function matchRepeatPath(pathD: string) {
    // 使用正则表达式找到第二个英文字母的位置
    const match = pathD.match(/[a-zA-Z]/g);
    if (match && match.length >= 2) {
        // 获取第二个英文字母的索引
        const secondLetterIndex = pathD.indexOf(match[1]);
        // 从该索引开始截取到结尾
        return pathD.substring(secondLetterIndex);
    }
    return pathD;
}

/**
 * 创建画笔图片
 *
 * @param pathD - 绘制路径
 * @param data - 图层数据
 */
export function createBrushImage(
    pathD: string,
    data: Pick<
        IBrushLayerProps,
        'brushType' | '__isDrawPreview' | 'fills' | 'stroke' | 'naturalWidth' | 'naturalHeight'
    > &
        Pick<ILayerProps, 'width' | 'height'>,
) {
    const { brushType, __isDrawPreview, fills, stroke, naturalWidth, naturalHeight, width, height } = data;

    const paths: string[] = [];
    const defs: string[] = [];
    const isPolygon = isPolygonSvg(brushType);

    for (let i = 0; i < fills.length; i++) {
        const fill = fills[i];
        if (i === 0) {
            if (isPolygon) {
                const offset = stroke.width;
                pathD = formatSvgPathDecimals(
                    svgPath(pathD)
                        .scale((width - offset) / naturalWidth, (height - offset) / naturalHeight)
                        .translate(offset / 2, offset / 2)
                        .toString(),
                );
                const res = createPathTarget(fill, pathD, width, height, defs, stroke);
                paths.push(res);
            } else {
                //
                // 计算第一条主画笔的路径
                //
                if (!__isDrawPreview) {
                    // 图层渲染时需要做偏移计算
                    const res = getPathOffset(fills, stroke);
                    const newWidth = width - res.offsetWidth;
                    const newHeight = height - res.offsetHeight;
                    // console.log('pathD => 1', pathD, width, res.offsetWidth);
                    pathD = formatSvgPathDecimals(
                        svgPath(pathD)
                            .scale(newWidth / naturalWidth, newHeight / naturalHeight)
                            .translate(res.translate, res.translate)
                            .toString(),
                    );
                    // console.log('pathD => 2', pathD);
                }
                const res = createPathTarget(fill, pathD, width, height, defs);
                paths.push(res);
            }
        } else {
            // 基于上一条路径做偏移，绘制多重画笔
            pathD = formatSvgPathDecimals(svgPath(pathD).translate(fill.offsetX, fill.offsetY).toString());
            const res = createPathTarget(fill, pathD, width, height, defs);
            paths.unshift(res);
        }
    }

    return createSvgImage(width, height, paths, defs);
}

/**
 * 生成绘制路径
 *
 * @param points - 绘制坐标点
 * @param epsilon - 可选；点数；默认值为 `1`
 */
export function generateBrushPath(points: IPoint[], epsilon = 1) {
    // console.log('epsilon => ', Math.max(1, epsilon));

    return {
        /** 曲线笔 */
        [BrushType.Curve]() {
            const data = epsilon ? simplifyPath(points, Math.max(1, epsilon)) : points;
            const brush = line()
                .curve(curveCatmullRom.alpha(0.7))
                .x((d: any) => d.x)
                .y((d: any) => d.y);
            return brush(data);
        },
        /** 直线笔 */
        [BrushType.Line]() {
            const startPoint = points[0];
            const endPoint = points[points.length - 1];
            return generateLinePath(startPoint, endPoint);
        },
        /** 箭头笔 */
        [BrushType.Arrow]() {
            return 'arrow';
        },
        /** 矩形 */
        [BrushType.Rect]() {
            return 'rect';
        },
        /** 椭圆 */
        [BrushType.Ellipse]() {
            return 'ellipse';
        },
        /** 多边形 */
        [BrushType.Polygon](path: string) {
            return path;
        },
    };
}

/**
 * 生成直线路径
 *
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 */
export function generateLinePath(startPoint: IPoint, endPoint: IPoint) {
    if (isNaN(startPoint?.x)) {
        console.log('generateLinePath', startPoint, endPoint);
    }

    return `M ${startPoint.x} ${startPoint.y} L ${endPoint.x} ${endPoint.y}`;
}

/**
 * 获取箭头画笔的最小尺寸
 *
 * @param fillWidth - 填充宽度
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 */
export function getArrowBrushMinSize(fillWidth: number, startPoint: IBrushPoint, endPoint: IBrushPoint) {
    // 计算新的宽高，素材规范为 4px 大小

    const startPointHeight = (startPoint.height / BRUSH_POINT_SIZE) * fillWidth;
    const endPointHeight = (endPoint.height / BRUSH_POINT_SIZE) * fillWidth;
    const minHeight = Math.max(startPointHeight, endPointHeight, fillWidth);

    const startPointWidth = (startPoint.width / BRUSH_POINT_SIZE) * fillWidth;
    const endPointWidth = (endPoint.width / BRUSH_POINT_SIZE) * fillWidth;
    const minWidth = Math.max(startPointWidth, endPointWidth);

    return {
        minHeight: Math.ceil(minHeight),
        minWidth: Math.ceil(minWidth),
    };
}

/**
 * 适配画笔的基础信息
 *
 * @param identity - 图层 ID
 * @param brushData - 画笔数据
 * @param scale - 缩放比例
 * @param stageIndex - 可选；舞台序号；默认值为 `0`
 */
const adapterBrushBaseInfo = (identity: string, brushData: IBrushDrawConfig) => {
    // 为了实现描边，获取舞台大小的画布
    function getFullCanvas(info) {
        // const { width, height } = getStageRect(identity, stageIndex);
        const { width, height } = getFramework(identity).service.editor.getBoundingClientRect();
        // info.width = width / scale;
        // info.height = height / scale;
        info.width = width;
        info.height = height;
        info.left = 0;
        info.top = 0;
        info.naturalWidth = info.width;
        info.naturalHeight = info.height;
        return info;
    }

    // 获取图形类型的基础信息
    // 圆形、正方形、多边形、心型、星型
    function getGraphicsInfo(info, size = 0) {
        const strokeWidth = brushData.stroke.width;
        const hotKeyDown = isHotKeyPress();
        info.width = ~~Math.max(strokeWidth, info.width);
        info.height = ~~Math.max(strokeWidth, info.height);
        if (hotKeyDown) {
            // 绘制等比例形状
            const drawSquareSize = Math.max(~~info.width, ~~info.height);
            info.width = drawSquareSize;
            info.height = drawSquareSize;
        }
        info.naturalWidth = size || info.width;
        info.naturalHeight = size || info.height;
        const { x1, x2, y1, y2 } = info;
        if (hotKeyDown) {
            if (x2 > x1) {
                info.left = x1;
            } else {
                info.left = x1 - info.width;
            }
            if (y2 > y1) {
                info.top = y1;
            } else {
                info.top = y1 - info.height;
            }
        }
        return info;
    }

    // 计算直线的旋转角度
    function getLineTransform(info: any) {
        const { maxY, maxX, top: minY, left: minX, x1, x2, y1, y2 } = info;
        const centerPoint = {
            x: (x2 - x1) / 2 + x1,
            y: (y2 - y1) / 2 + y1,
        };
        const start = {
            x: x2 - x1,
            y: y2 - y1,
        };
        const rotate = -getPointsBetweenAngle(start, { x: 1, y: 0 });
        const line = Math.pow(maxY - minY, 2) + Math.pow(maxX - minX, 2);
        return {
            centerPoint,
            rotate: getActualAngle(rotate),
            width: +Math.sqrt(line).toFixed(0),
        };
    }
    // 获取直线的基础信息
    function getLineBaseInfo(info, height = 0, minWidth = 0) {
        const { centerPoint, rotate, width } = getLineTransform(info);
        info.width = Math.max(width, minWidth);
        info.height = height;
        info.top = centerPoint.y - info.height / 2;
        info.left = centerPoint.x - info.width / 2;
        info.rotate = rotate;
        return info;
    }

    return {
        /** 曲线笔 */
        [BrushType.Curve](info) {
            return getFullCanvas(info);
        },
        /** 直线笔 */
        [BrushType.Line](info) {
            // if (isDrawPreview) {
            //     console.log('getFullCanvas', info, isDrawPreview);
            //     return getFullCanvas(info);
            // }
            return getLineBaseInfo(info);
        },
        /** 波浪笔 */
        [BrushType.Wavy](info, size) {
            return getLineBaseInfo(info, size.height);
        },
        /** 箭头笔 */
        [BrushType.Arrow](info) {
            const { minHeight, minWidth } = getArrowBrushMinSize(
                brushData.fills[0].width,
                brushData.startPoint,
                brushData.endPoint,
            );

            return getLineBaseInfo(info, minHeight, minWidth);
        },
        /** 矩形 */
        [BrushType.Rect](info) {
            return getGraphicsInfo(info);
        },
        /** 椭圆 */
        [BrushType.Ellipse](info) {
            return getGraphicsInfo(info);
        },
        /** 心形,星形,三角形 */
        [BrushType.Polygon](info) {
            /** 素材大小 200 * 200 */
            return getGraphicsInfo(info, 200);
        },
    };
};

/**
 * 获取绘制的基础信息
 *
 * @param brushData - 画笔数据
 * @param scale - 缩放比例
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 * @param stageIndex - 可选；舞台序号；默认值为 `0`
 * @param isDrawPreview - 可选；是否绘制预览；默认值为 `true`
 * @param size - 可选；尺寸
 */
export function getBrushBaseInfo(
    identity: string,
    brushData: IBrushDrawConfig,
    startPoint: IPoint,
    endPoint: IPoint,
    size?: ISize,
) {
    const { x: x1, y: y1 } = startPoint;
    const { x: x2, y: y2 } = endPoint;

    const maxX = max([x1, x2]) as number;
    const minX = min([x1, x2]) as number;
    const maxY = max([y1, y2]) as number;
    const minY = min([y1, y2]) as number;
    const info = {
        width: maxX - minX,
        height: maxY - minY,
        left: minX,
        top: minY,
        rotate: 0,
        maxX,
        maxY,
        x1,
        x2,
        y1,
        y2,
        naturalWidth: maxX - minX,
        naturalHeight: maxY - minY,
    };

    // 重新适配基础信息
    adapterBrushBaseInfo(identity, brushData)[brushData.brushType](info, size);
    return info;
}

/**
 * 添加 VIP 标记
 *
 * @param layer - 图层属性
 * @param config - 画笔配置
 */
export function addVipMaterialIds(layer: ILayerProps, config?: IBrushDrawConfig | null) {
    if (config?.__vipFuncType) {
        // 添加 vip 标记
        if (!layer.materialIds) {
            layer.materialIds = {};
        }
        const funcList = layer.materialIds[MaterialIdsType.FUNC_LIST] || [];
        funcList.push(config.__vipFuncType);
        layer.materialIds[MaterialIdsType.FUNC_LIST] = funcList;
    }
}
